<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { avatarProps } from 'naive-ui';
import { gaiaFontWeight, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  ...avatarProps,
  /** user = initials circle, mark = square workspace/agent mark (radius 8). */
  variant: {
    type: String as PropType<'user' | 'mark'>,
    default: 'user',
  },
  size: {
    type: [Number, String] as PropType<number | 'small' | 'medium' | 'large'>,
    default: 28,
  },
});

// `variant` is ours, not n-avatar's — keep it off the DOM element.
const passThrough = computed(() => {
  const { variant, ...rest } = props;
  return rest;
});

const isMark = computed(() => props.variant === 'mark');

const numericSize = computed(() => (typeof props.size === 'number' ? props.size : 28));
// Initials scale derived from the two mock 5a sizes (user 28→10.5, mark 28→13).
const fontSize = computed(
  () => `${Math.round(numericSize.value * (isMark.value ? 0.46 : 0.375) * 2) / 2}px`
);

const mergedThemeOverrides = computed(() => ({
  ...(isMark.value ? { borderRadius: '8px' } : {}),
  ...props.themeOverrides,
}));
</script>

<template>
  <n-avatar
    v-bind="passThrough"
    :round="!isMark"
    :color="props.color ?? (isMark ? gaiaNeutral.ink : gaiaNeutral.surfaceMuted)"
    :theme-overrides="mergedThemeOverrides"
    :style="{
      color: isMark ? gaiaNeutral.surface : gaiaNeutral.text,
      fontWeight: gaiaFontWeight.bold,
      fontSize,
    }"
  >
    <slot />
  </n-avatar>
</template>
